import { useActiveOrganization } from './use-organization'
import { useCallback, useLayoutEffect, useState } from 'react'
import { twinsGetNote, twinsNotes } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { TwinNoteSummary } from 'api/model/twinNoteSummary'
import { TwinNote } from 'api/model/twinNote'

function useFetchFullNote(noteId: string) {
  const [activeOrganization] = useActiveOrganization()
  const [fullNote, setFullNote] = useState<TwinNote | null>(null)

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useLayoutEffect(() => {
    async function fetchData() {
      if (!activeOrganization || !noteId) {
        return
      }

      setIsLoading(true)

      try {
        const response = await twinsGetNote(activeOrganization, noteId)

        setFullNote(response.data ? response.data : null)
      } catch (error) {
        setError(`${error}`)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [activeOrganization, setFullNote, noteId])

  return {
    isLoading,
    fullNote,
    error,
    setFullNote,
  }
}

function useFetchNotes() {
  const [activeOrganization] = useActiveOrganization()
  const [notes, setNotes] = useState<TwinNoteSummary[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refetchNotes = useCallback(async () => {
    if (!activeOrganization) {
      return
    }

    setIsLoading(true)

    try {
      const response = await twinsNotes(activeOrganization)

      setNotes(response.data ? response.data : [])
    } catch (error) {
      setError(`${error}`)
    } finally {
      setIsLoading(false)
    }
  }, [activeOrganization, setNotes])

  useLayoutEffect(() => {
    refetchNotes()
  }, [refetchNotes])

  return {
    isLoading,
    notes,
    error,
    setNotes,
    refetchNotes,
  }
}

export { useFetchNotes, useFetchFullNote }
